/**
 * What prose can be dressed in beyond bold, italic and lists, as Tiptap
 * extensions: underline, superscript and subscript, a colour, a highlight, a
 * size, and a paragraph's alignment.
 *
 * Underline and the scripts are their own elements (`u`, `sup`, `sub`), which
 * the sanitiser keeps. Everything else is a class from the palette in
 * `domain/prose.ts`, never an inline style, which the sanitiser strips: a
 * colour survives saving, sharing and exporting because the stylesheet knows
 * its name. A span carrying several classes gives each mark its own, since no
 * rule here consumes the span for the others.
 */
import { Extension, Mark, mergeAttributes } from '@tiptap/core'

import {
  PROSE_ALIGNS,
  PROSE_INKS,
  PROSE_MARKS,
  PROSE_SIZES,
  proseClassValue,
  type ProseAlign,
  type ProseInk,
  type ProseMark,
  type ProseSize,
} from '@/domain/prose'

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    proseUnderline: { toggleUnderline: () => ReturnType }
    proseSuperscript: { toggleSuperscript: () => ReturnType }
    proseSubscript: { toggleSubscript: () => ReturnType }
    proseInk: { setInk: (name: ProseInk) => ReturnType; unsetInk: () => ReturnType }
    proseHighlight: {
      setHighlight: (name: ProseMark) => ReturnType
      unsetHighlight: () => ReturnType
    }
    proseSize: { setSize: (name: ProseSize) => ReturnType; unsetSize: () => ReturnType }
    proseAlign: { setAlign: (name: ProseAlign) => ReturnType; unsetAlign: () => ReturnType }
  }
}

export const Underline = Mark.create({
  name: 'underline',
  parseHTML: () => [{ tag: 'u' }],
  renderHTML: ({ HTMLAttributes }) => ['u', mergeAttributes(HTMLAttributes), 0],
  addCommands() {
    return {
      toggleUnderline:
        () =>
        ({ commands }) =>
          commands.toggleMark(this.name),
    }
  },
  addKeyboardShortcuts() {
    return { 'Mod-u': () => this.editor.commands.toggleUnderline() }
  },
})

/** Raised and lowered text exclude each other: a letter is one or the other. */
export const Superscript = Mark.create({
  name: 'superscript',
  excludes: 'subscript',
  parseHTML: () => [{ tag: 'sup' }],
  renderHTML: ({ HTMLAttributes }) => ['sup', mergeAttributes(HTMLAttributes), 0],
  addCommands() {
    return {
      toggleSuperscript:
        () =>
        ({ commands }) =>
          commands.toggleMark(this.name),
    }
  },
})

export const Subscript = Mark.create({
  name: 'subscript',
  excludes: 'superscript',
  parseHTML: () => [{ tag: 'sub' }],
  renderHTML: ({ HTMLAttributes }) => ['sub', mergeAttributes(HTMLAttributes), 0],
  addCommands() {
    return {
      toggleSubscript:
        () =>
        ({ commands }) =>
          commands.toggleMark(this.name),
    }
  },
})

/** A mark written as one class of a family: `ink-red`, `mark-yellow`, `size-large`. */
function classMark(name: string, prefix: 'ink' | 'mark' | 'size', names: readonly string[]) {
  return Mark.create({
    name,
    addAttributes() {
      return { value: { default: null } }
    },
    parseHTML() {
      return [
        {
          tag: 'span',
          consuming: false,
          getAttrs: (element) => {
            if (!(element instanceof HTMLElement)) return false
            for (const className of element.classList) {
              const value = proseClassValue(prefix, names, className)
              if (value) return { value }
            }
            return false
          },
        },
      ]
    },
    renderHTML({ mark, HTMLAttributes }) {
      const value = mark.attrs['value'] as string | null
      return [
        'span',
        mergeAttributes(HTMLAttributes, value ? { class: `${prefix}-${value}` } : {}),
        0,
      ]
    },
  })
}

export const InkMark = classMark('proseInk', 'ink', PROSE_INKS).extend({
  addCommands() {
    return {
      setInk:
        (value: ProseInk) =>
        ({ commands }) =>
          commands.setMark(this.name, { value }),
      unsetInk:
        () =>
        ({ commands }) =>
          commands.unsetMark(this.name),
    }
  },
})

export const HighlightMark = classMark('proseHighlight', 'mark', PROSE_MARKS).extend({
  addCommands() {
    return {
      setHighlight:
        (value: ProseMark) =>
        ({ commands }) =>
          commands.setMark(this.name, { value }),
      unsetHighlight:
        () =>
        ({ commands }) =>
          commands.unsetMark(this.name),
    }
  },
})

export const SizeMark = classMark('proseSize', 'size', PROSE_SIZES).extend({
  addCommands() {
    return {
      setSize:
        (value: ProseSize) =>
        ({ commands }) =>
          commands.setMark(this.name, { value }),
      unsetSize:
        () =>
        ({ commands }) =>
          commands.unsetMark(this.name),
    }
  },
})

/**
 * A paragraph's alignment, as an `align-*` class on it. Any other class a
 * paragraph came with (an imported paragraph's justified lines, say) is kept
 * as it was, so editing one word does not undo how the paragraph was set.
 */
export const ParagraphAlign = Extension.create({
  name: 'proseAlign',
  addGlobalAttributes() {
    return [
      {
        types: ['paragraph'],
        attributes: {
          align: {
            default: null,
            parseHTML: (element) => {
              for (const className of element.classList) {
                const value = proseClassValue('align', PROSE_ALIGNS, className)
                if (value) return value
              }
              return null
            },
            renderHTML: (attributes) =>
              attributes['align'] ? { class: `align-${String(attributes['align'])}` } : {},
          },
          keptClass: {
            default: null,
            parseHTML: (element) =>
              [...element.classList].filter((name) => !name.startsWith('align-')).join(' ') || null,
            renderHTML: (attributes) =>
              attributes['keptClass'] ? { class: String(attributes['keptClass']) } : {},
          },
        },
      },
    ]
  },
  addCommands() {
    return {
      setAlign:
        (value: ProseAlign) =>
        ({ commands }) =>
          commands.updateAttributes('paragraph', { align: value }),
      unsetAlign:
        () =>
        ({ commands }) =>
          commands.resetAttributes('paragraph', 'align'),
    }
  },
  addKeyboardShortcuts() {
    return {
      'Mod-Shift-l': () => this.editor.commands.setAlign('left'),
      'Mod-Shift-e': () => this.editor.commands.setAlign('center'),
      'Mod-Shift-r': () => this.editor.commands.setAlign('right'),
      'Mod-Shift-j': () => this.editor.commands.setAlign('justify'),
    }
  },
})

/** Everything above, to hand to an editor in one go. */
export const PROSE_EXTENSIONS = [
  Underline,
  Superscript,
  Subscript,
  InkMark,
  HighlightMark,
  SizeMark,
  ParagraphAlign,
]
